import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import { SSRConfig } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { FaUserAstronaut } from 'react-icons/fa';
import { HiCollection } from 'react-icons/hi';

import { getCountAds, GetAdsByUser } from '@/api/fetch/marketplace-api';
import { getUser } from '@/api/fetch/rest-marketplace-api';
import { APP_CONFIG } from '@/app-config';
import { LIMIT } from '@/lib/constants';
import { cleanObject } from '@/lib/utils';
import Layout from '@/modules/core/components/Layout';
import UserListings from '@/modules/core/components/UserListings';
import { mapUserResponseToIUserData } from '@/modules/core/mappings';
import {
  FilterPrivateAdsType,
  IListingItem,
  IUserData,
  ListingState,
} from '@/modules/core/types';
import { Meta } from '@/seo/Meta';
import Text from '@/tayara-kit/Text';

interface UserPublicPageProps extends SSRConfig {
  userData: IUserData;
  countAdByUser: number;
  adsByUser: IListingItem[];
}

const UserPublicPage = ({
  userData,
  countAdByUser,
  adsByUser,
}: UserPublicPageProps) => {
  const userAvatar = userData.avatar || '';
  return (
    <div className="mt-[48px]">
      {/* shops cover photo */}
      <div className="w-full relative">
        <div className="h-36"></div>

        {/* user icon pattern */}
        <div className="absolute inset-0 dotted-pattern opacity-80 z-[-1]"></div>
        {/* Graidnet */}
        <div className="bg-gradient-to-br bg-gray-200 absolute inset-0 z-[-2]"></div>

        {/* User avatar */}
        <div className="avatar absolute bottom-0 left-1/2 md:left-0 md:ml-6 -translate-x-1/2 md:translate-x-0 translate-y-1/3 md:translate-y-2/3">
          <div className="z-[-3] bg-white w-40 absolute mask mask-squircle"></div>
          <div className="z-[-2] m-2 bg-red-100 w-36 absolute mask mask-squircle"></div>
          <div className="w-36 m-2 mask mask-squircle text-primary grid relative z-[-1]">
            {userData.avatar ? (
              <img
                src={
                  userAvatar?.includes('http')
                    ? `https://www.tayara.tn/mediaGateway/resize-image?img=${userAvatar.substring(
                        userAvatar.lastIndexOf('/') - 2
                      )}&w=100`
                    : `https://www.tayara.tn/mediaGateway/resize-image?img=${userAvatar}&w=100`
                }
                className="w-70 mx-auto"
                alt={`tayara user avatar of ${userData.fullname}`}
              />
            ) : (
              <FaUserAstronaut
                size={70}
                className="m-auto h-full -mt-1 filter"
              />
            )}
          </div>
        </div>
      </div>

      {/* Metadata and social */}
      <div className="md:h-20 mt-14 md:mt-0 gap-3 flex flex-col md:flex-row justify-between">
        <div className="md:ml-52 mt-1 flex flex-col md:items-start items-center">
          <Text
            text={userData.fullname || ''}
            className="text-gray-700 font-bold text-xl"
          />
          <span className="text-gray-700 flex items-center  text-2xs gap-2">
            <HiCollection size={13} className="text-gray-600" />
            <Text text={`${countAdByUser}annonces`} />
          </span>
        </div>
        {/* Social actions */}
        {/* TODO report user */}
        {/* <div className="flex gap-4 mt-2 md:mr-3 md:mx-auto md:flex-row flex-col items-center md:items-start">
          <button
            aria-label="Signaler"
            className="btn btn-sm rounded-md normal-case btn-ghost text-gray-600 space-x-1 hover:bg-transparent hover:text-error"
          >
            <IoChatbox size={18} /> <Text text="Signaler" />
          </button>
        </div> */}
      </div>

      <div className="mt-4 md:mt-16">
        <UserListings
          adsByUser={adsByUser}
          countAdByUser={countAdByUser}
          userId={userData.id || ''}
        />
      </div>
    </div>
  );
};

export default UserPublicPage;

// Layout configuration
UserPublicPage.getLayout = function getLayout(page: JSX.Element) {
  return (
    <Layout>
      <Meta
        description="page boutique"
        title={`${APP_CONFIG.site_name} | page boutique`}
      />
      {page}
    </Layout>
  );
};

// Server side code and i18n
export const getServerSideProps: GetServerSideProps = async ({
  locale,
  query,
  res,
}): Promise<GetServerSidePropsResult<UserPublicPageProps>> => {
  const { userId } = query;

  const localeData = await serverSideTranslations(locale || 'en');

  if (typeof userId !== 'string' || !userId) {
    res.statusCode = 400;
    return {
      notFound: true,
    };
  }

  const filterParams: FilterPrivateAdsType = {
    userId,
    isPublic: true,
    limit: LIMIT,
    skip: 0,
    status: 2,
    category: '',
  };
  const [user, adsByUser, countAdByUser] = await Promise.all([
    getUser(userId),
    GetAdsByUser(filterParams),
    getCountAds(filterParams),
  ]);

  return {
    props: cleanObject({
      ...localeData,
      userData: mapUserResponseToIUserData(user),
      countAdByUser: countAdByUser.getCount(),
      adsByUser: adsByUser.flatMap((listingItem) =>
        // Deleted items will not be listed
        listingItem.metadata?.state === ListingState.DELETED
          ? []
          : [listingItem]
      ),
    }),
  };
};
